import { useState } from 'react';
import { Popover } from '@base-ui/react/popover';
import { Checkbox } from '@components/ui/Checkbox/Checkbox';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { SearchInput } from '@components/ui/SearchInput/SearchInput';
import { ChevronDownIcon } from '@utils/icons';
import { fold } from '@utils/text';
import styles from './CheckListPick.module.css';

export interface ICheckListOption {
  /** Stable identity handed back on toggle. */
  value: string;
  label: string;
  /** Faded second line under the label (an email, the floor an area is on). */
  hint?: string;
}

/** A "select everything" row above the list. Its meaning is the caller's: the
 *  caller decides what `checked` is and what toggling it does. */
export interface ICheckListAll {
  label: string;
  checked: boolean;
  onToggle: (next: boolean) => void;
}

export interface ICheckListPickProps {
  /** What the closed trigger reads ("3 recipients", "Floor 2, Floor 3"). Empty: the placeholder, faded. */
  summary: string;
  placeholder: string;
  ariaLabel: string;
  options: ICheckListOption[];
  selected: ReadonlySet<string>;
  onToggle: (value: string, next: boolean) => void;
  all?: ICheckListAll;
  /** Set: a search box above the rows narrows them (case- and width-insensitive, label or hint). */
  searchPlaceholder?: string;
  /** Shown when there is no row to show — none at all, or none the search matches. */
  noMatches: string;
}

/**
 * The multi-pick: a trigger wearing PickCombo's field skin that opens a
 * popover of checkbox rows, optionally filtered by a search box. Each toggle
 * is reported on its own (`onToggle`), so a host that saves per pick (the
 * Alerts Hub's recipients) and a host that collects a draft (a dialog) use
 * the same control. The popover is lifted above dialogs, so it works inside
 * `ui/Modal` too.
 */
export function CheckListPick({
  summary,
  placeholder,
  ariaLabel,
  options,
  selected,
  onToggle,
  all,
  searchPlaceholder,
  noMatches,
}: ICheckListPickProps) {
  const [query, setQuery] = useState('');
  const needle = fold(query.trim());
  const shown = options.filter((o) => fold(`${o.label} ${o.hint ?? ''}`).includes(needle));
  const triggerCls = summary ? styles.trigger : `${styles.trigger} ${styles.placeholder}`;

  return (
    <Popover.Root onOpenChange={(open) => { if (!open) setQuery(''); }}>
      <Popover.Trigger type="button" aria-label={ariaLabel} className={triggerCls}>
        {summary || placeholder}
        <span className={styles.caret} aria-hidden="true">
          <ChevronDownIcon size={12} />
        </span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner sideOffset={4} align="start" collisionPadding={8} className={styles.positioner}>
          <Popover.Popup className={styles.popup}>
            {searchPlaceholder !== undefined && (
              <SearchInput
                value={query}
                onChange={setQuery}
                placeholder={searchPlaceholder}
                ariaLabel={searchPlaceholder}
                className={styles.search}
              />
            )}
            {all && (
              <label className={`${styles.row} ${styles.allRow}`}>
                <Checkbox checked={all.checked} onCheckedChange={(next) => all.onToggle(next)}ariaLabel={all.label} />
                <span className={styles.label}>{all.label}</span>
              </label>
            )}
            <ScrollArea className={styles.rows}>
              {shown.length === 0 ? (
                <p className={styles.empty}>{noMatches}</p>
              ) : (
                shown.map((o) => (
                  <label key={o.value} className={styles.row}>
                    <Checkbox
                      checked={selected.has(o.value)}
                      onCheckedChange={(next) => onToggle(o.value, next)}
                      ariaLabel={o.label}
                    />
                    <span className={styles.label}>
                      {o.label}
                      {o.hint && <span className={styles.hint}>{o.hint}</span>}
                    </span>
                  </label>
                ))
              )}
            </ScrollArea>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
